import { ActionIcon, Badge, Button, Group, HoverCard, Modal, MultiSelect, Stack, Text, Tooltip } from '@mantine/core';
import { IconAdjustmentsHorizontal, IconCornerDownLeft } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { LookupRow } from '../api/types';
import { LookupManager } from '../components/LookupManager';
import { useToast } from '../components/Toast';
import { nameOf, tr } from '../lib/i18n';

export function FeaturesPage() {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const types = useQuery({ queryKey: ['property-types', 'all'], queryFn: () => api<{ items: LookupRow[] }>('/property-types', { query: { includeInactive: true } }) });
  const groups = useQuery({ queryKey: ['feature-groups', 'all'], queryFn: () => api<{ items: LookupRow[] }>('/feature-groups', { query: { includeInactive: true } }) });
  // the features a detail can belong to: main features (not details themselves)
  const mains = useQuery({ queryKey: ['property-features', 'mains'], queryFn: () => api<{ items: LookupRow[] }>('/property-features') });
  const groupOptions = (groups.data?.items ?? []).map((g) => ({ value: g.id, label: nameOf(g) }));
  const mainOptions = (mains.data?.items ?? []).filter((f) => !f.parent_id).map((f) => ({ value: f.id, label: nameOf(f) }));
  const groupName = (id?: number | null) => nameOf(groups.data?.items.find((g) => g.id === id)) || tr('أخرى', 'Other');
  const mainName = (id?: number | null) => nameOf(mains.data?.items.find((f) => f.id === id));
  const [editing, setEditing] = useState<{ feature: LookupRow; ids: string[] } | null>(null);
  const typeName = (id: number) => {
    const t = types.data?.items.find((x) => x.id === id);
    return t ? nameOf(t) : String(id);
  };

  const saveTypes = useMutation({
    mutationFn: () => api(`/property-features/${editing!.feature.id}/types`, { method: 'PUT', body: { propertyTypeIds: editing!.ids.map(Number) } }),
    onSuccess: () => {
      toast(tr('تم تحديث الأنواع المرتبطة', 'Linked types updated'));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['property-features'] });
    },
    onError: toastError,
  });

  return (
    <>
      <LookupManager
        path="property-features"
        queryKey="property-features"
        title={tr('مميزات العقار', 'Property features')}
        description={tr('تظهر في خطوة المميزات حسب نوع العقار؛ التفاصيل تظهر تحت ميزتها عند اختيارها', 'Shown in the features step by property type; details appear under their feature once it is picked')}
        addLabel={tr('ميزة جديدة', 'New feature')}
        extraFields={[
          { key: 'groupId', column: 'group_id', label: tr('المجموعة', 'Group'), description: tr('العنوان الذي تظهر تحته في خطوة المميزات', 'The heading it appears under in the features step'), options: groupOptions, numeric: true },
          { key: 'parentId', column: 'parent_id', label: tr('تفصيل لميزة', 'Detail of a feature'), description: tr('اختياري — مثل «ثلاجة» تفصيل لـ«أجهزة كهربائية»', 'Optional — e.g. «Fridge» under «Appliances»'), options: mainOptions, numeric: true },
        ]}
        filter={{ key: 'groupId', column: 'group_id', label: tr('المجموعة', 'Group'), options: groupOptions }}
        extraColumns={[
          {
            label: tr('المجموعة', 'Group'),
            sortValue: (r) => (r.parent_id ? `~${mainName(r.parent_id)}` : groupName(r.group_id)),
            render: (r) =>
              r.parent_id ? (
                <Group gap={6} wrap="nowrap">
                  <IconCornerDownLeft size={14} color="var(--pf-muted)" />
                  <Text size="sm" c="dimmed">{tr('تفصيل لـ', 'Detail of')} <Text span c="var(--pf-text)" fw={500}>{mainName(r.parent_id)}</Text></Text>
                </Group>
              ) : (
                <Text size="sm">{groupName(r.group_id)}</Text>
              ),
          },
          {
            label: tr('تنطبق على', 'Applies to'),
            sortValue: (r) => (r.parent_id ? -1 : r.property_type_ids?.length ?? 0),
            render: (r) => {
              if (r.parent_id) return <Text size="sm" c="dimmed">{tr('مثل ميزتها', 'Same as its feature')}</Text>;
              const ids = r.property_type_ids ?? [];
              if (!ids.length) return <Badge variant="light" color="teal" radius="sm" fw={500}>{tr('كل الأنواع', 'All types')}</Badge>;
              return (
                <HoverCard width={280} shadow="md" position="bottom-start" withinPortal>
                  <HoverCard.Target>
                    <Badge variant="default" radius="sm" fw={500} style={{ cursor: 'help' }}>{tr(`${ids.length} أنواع`, `${ids.length} types`)}</Badge>
                  </HoverCard.Target>
                  <HoverCard.Dropdown>
                    <Group gap={4}>{ids.map((id) => <Badge key={id} variant="light" color="gray" radius="sm" fw={500}>{typeName(id)}</Badge>)}</Group>
                  </HoverCard.Dropdown>
                </HoverCard>
              );
            },
          },
        ]}
        rowActions={(r) => r.parent_id ? null : (
          <Tooltip label={tr('أنواع العقار المرتبطة', 'Linked property types')}>
            <ActionIcon onClick={() => setEditing({ feature: r, ids: (r.property_type_ids ?? []).map(String) })} aria-label={tr('أنواع العقار المرتبطة', 'Linked property types')}>
              <IconAdjustmentsHorizontal size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      />
      <Modal opened={!!editing} onClose={() => setEditing(null)} size={520} title={<>{tr('أنواع العقار لـ', 'Property types for')} «{nameOf(editing?.feature)}»</>}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">{tr('اتركها فارغة لتظهر الميزة مع كل أنواع العقار.', 'Leave empty to show the feature for all property types.')}</Text>
          <MultiSelect
            label={tr('أنواع العقار', 'Property types')}
            searchable
            clearable
            placeholder={editing?.ids.length ? undefined : tr('كل الأنواع', 'All types')}
            value={editing?.ids ?? []}
            onChange={(v) => editing && setEditing({ ...editing, ids: v })}
            data={(types.data?.items ?? []).map((t) => ({ value: String(t.id), label: nameOf(t) }))}
            maxDropdownHeight={280}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setEditing(null)}>{tr('إلغاء', 'Cancel')}</Button>
            <Button onClick={() => saveTypes.mutate()} loading={saveTypes.isPending}>{tr('حفظ', 'Save')}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
